import React from 'react';
import {
	Alert,
	FormControl,
	Checkbox,
	FormGroup,
	Button,
	ControlLabel,
	Radio,
	Tabs,
	Tab,
	OverlayTrigger,
	Tooltip,
	ToggleButton,
	ToggleButtonGroup
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import {map} from 'lodash';
import { t } from 'i18next';
import moment from 'moment';
import dispatchCustomEvent from '../../services/CustomEvents';
import { ImageSelection } from '../ImageSelection';
import { ImageUploader } from '../ImageUploader';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { RichTextEditor } from '../RichTextEditor';

import 'react-datepicker/dist/react-datepicker.css';
import {CirclePicker} from "react-color";

const FormField = ({field}) => {
	if (!field) {
		return null;
	}
	// Give controllers an opportunity to fully render the fields themselves.  This was helpful for the password reset
	// field where we needed a reference to the ReactPasswordStrength field.
	if (field.field) {
		return field.field;
	}

	const formControl = ( field => {
		switch (field.type) {
      case "notification":
        return <Alert bsStyle={field.value}>{field.message}</Alert>;
      case "images":
        const imageChoices = field.choices
          ? map(field.choices, (choiceName, choiceValue) => (
              <Radio
                value={choiceValue}
                name={field.name}
                key={choiceValue}
              >
                <span dangerouslySetInnerHTML={{ __html: choiceName }} />
              </Radio>
            ))
          : "";
        return <span className="images">{imageChoices}</span>;
      case "radio":
        let radioChoices = null;

        // If there are only two options display as a Radio Button Group.
        switch (Object.keys(field.choices).length) {
          case 2:
          case 3:
            radioChoices = map(field.choices, (choiceName, choiceValue) => (
              <ToggleButton
                bsStyle="info"
                value={choiceValue}
                key={choiceValue}
              >
                {choiceName}
              </ToggleButton>
            ));
            return (
              <FormGroup>
                <ToggleButtonGroup
                  justified
                  name={field.name}
                  defaultValue={field.value}
                >
                  {radioChoices}
                </ToggleButtonGroup>
              </FormGroup>
            );
          default:
            radioChoices = field.choices
              ? map(field.choices, (choiceName, choiceValue) => (
                  <Radio
                    value={choiceValue}
                    key={choiceValue}
                    name={field.name}
                    defaultChecked={field.value === choiceValue}
                  >
                    {choiceName}
                  </Radio>
                ))
              : "";
            return <FormGroup>{radioChoices}</FormGroup>;
        }
      case "checkbox":
        const checkboxChoices = field.choices
          ? map(field.choices, (choiceName, choiceValue) => (
              <Checkbox
                value={choiceValue}
                key={choiceValue}
                name={field.name}
                defaultChecked={
                  field.value &&
                  (typeof field.value === "string"
                    ? field.value === choiceValue
                    : field.value.indexOf(choiceValue) !== -1)
                }
              >
                {choiceName}
              </Checkbox>
            ))
          : "";
        if (checkboxChoices.length > 10) {
          return (
            <details>
              <summary>
                {t("Show All")} ({field.value ? field.value.length : "0"}{" "}
                {t("Selected")})
              </summary>
              <FormGroup>{checkboxChoices}</FormGroup>
            </details>
          );
        } else {
          return <FormGroup>{checkboxChoices}</FormGroup>;
        }
      case "select":
        const selectChoices = field.choices
          ? map(field.choices, (choiceName, value) => (
              <option value={value} key={value}>
                {choiceName}
              </option>
            ))
          : "";
        return (
          <FormControl
            componentClass={field.type}
            placeholder={field.type}
            id={field.key}
            name={field.name}
            required={!!field.required}
            defaultValue={field.value}
          >
            {selectChoices}
          </FormControl>
        );
      case "textarea":
        return (
          <FormControl
            componentClass="textarea"
            id={field.key}
            name={field.name}
            placeholder={field.placeholder}
            defaultValue={field.value}
          />
        );
      case "wysiwyg":
        return (
          <div>
            <input
              type="hidden"
              id={field.key}
              defaultValue={field.value}
              name={field.name}
            />
            <RichTextEditor
              placeholder={field.placeholder}
              onChange={(content) => {
                const element = document.getElementById(field.key);
                element.value = content;
                dispatchCustomEvent("change", element, { bubbles: true });
              }}
              defaultValue={field.value}
            />
          </div>
        );
      case "color_picker":
        field.hex = field.value;
        return (
          <div>
            <input
              type="hidden"
              id={field.key}
              defaultValue={field.value}
              name={field.name}
            />
            <CirclePicker
              color={field}
              onChangeComplete={(content) => {
                const element = document.getElementById(field.key);
                element.value = content.hex;
                dispatchCustomEvent("change", element, { bubbles: true });
              }}
            />
          </div>
        );
      case "date_picker":
        return (
          <DatePicker
            id={field.key}
            selected={field.value ? moment(field.value) : null}
            name={field.name}
            isClearable
            onChange={(date) => {
              const element = document.getElementById(field.key);
              element.value = date ? date.format("YYYY-MM-DD") : "";
              dispatchCustomEvent("change", element, { bubbles: true });
            }}
            onChangeRaw={(e) => e.preventDefault()} // this prevents user from being able to type in the input field
          />
        );
      case "image":
        return field.choices ? (
          <ImageSelection field={field} onChange={field.onChange} />
        ) : (
          <ImageUploader field={field} onChange={field.onChange} />
        );
      case "file":
        return (
          <FormControl
            componentClass="input"
            type="file"
            id={field.key}
            name={field.name}
            placeholder={field.placeholder}
          />
        );
      case "message":
        return <p>{field.message}</p>;
      case "group":
        return (
          <div className="tbk--form-fields--group">
            {field.sub_fields.map((sub_field) => {
              return (
                <FormField
                  key={sub_field.key}
                  field={{
                    ...sub_field,
                    name: field.name + "[" + sub_field.name + "]",
                  }}
                />
              );
            })}
          </div>
        );
      case "relationship":
        return <div>Still need to implement this type of field</div>;
      case "gallery":
        if (field.choices) {
          return (
            <FormGroup>
              {map(field.choices, (image, ID) => {
                let selected = false;
                (field.value || []).forEach((current) => {
                  if (current.ID === image.ID) {
                    selected = true;
                  }
                });
                const onClick = () => {
                  field.onChange(image, field, !selected);
                };
                const wrapperClasses = classNames({
                  "tbk--gallery-field--selection": true,
                  selected,
                });
                return (
                  <React.Fragment key={ID}>
                    {selected && (
                      <input
                        type="hidden"
                        name={field.name + "[]"}
                        value={ID}
                      />
                    )}
                    <Button
                      type="button"
                      onClick={onClick}
                      className={wrapperClasses}
                    >
                      <img src={image.url} alt="Click to select" />
                    </Button>
                  </React.Fragment>
                );
              })}
            </FormGroup>
          );
        } else {
          return (
            <div>Don't know how to render a plain gallery yet. Sorry.</div>
          );
        }
      case "repeater":
        const repeaterVal = field.value || [];
        switch (field.name) {
          case "promo_details":
            return (
              <div>
                {repeaterVal.map((repeaterField, index) => {
                  return (
                    <>
                      <div key={index}>
						<small>Detail {index + 1}</small>
                        <FormControl
                          componentClass="input"
                          type="text"
                          id={field.key + "-" + index}
                          name={field.name + "-" + index}
                          defaultValue={repeaterField.promo_detail_name}
                        />
                      </div>
                      <br />
                    </>
                  );
                })}
              </div>
            );
          case "promo_faq":
            return (
              <div>
                {repeaterVal.map((repeaterField, index) => {
                  return (
                    <>
                      <div key={index}>
						<small>Question {index + 1}</small>
                        <FormControl
                          componentClass="input"
                          type="text"
                          id={field.key + "-" + index}
                          name={field.name + "-" + index}
                          defaultValue={repeaterField.promo_faq_question}
                        />
						<small>Answer {index + 1}</small>
                        <FormControl
                          componentClass="textarea"
                          id={field.key + "-" + index}
                          name={field.name + "-" + index}
                          defaultValue={repeaterField.promo_faq_answer}
                        />
                      </div>
                      <br />
                    </>
                  );
                })}
              </div>
            );
        }
      case "link":
      case "text":
      case "number":
      default:
        let type;
        switch (field.type) {
          case "link":
            type = "url";
            break;
          case "number":
          case "hidden":
            type = field.type;
            break;
          case "text":
          default:
            type = "text";
        }
        return (
          <FormControl
            componentClass="input"
            type={type}
            id={field.key}
            name={field.name}
            placeholder={field.placeholder}
            maxLength={field.maxlength}
            defaultValue={field.value}
          />
        );
    }
	})(field);

	const tooltip = field.instructions
		? <Tooltip id={field.name + '-instructions'}>{field.instructions}</Tooltip>
		: null;

	return ( 'hidden' === field.type ) ? formControl : <FormGroup>
			<ControlLabel>{field.label} {tooltip && <OverlayTrigger placement="top" overlay={tooltip}><i className="icon icon-info"/></OverlayTrigger>}</ControlLabel>
			{formControl}
		</FormGroup>;
}

class FormFields extends React.Component {
	static propTypes = {
		fields: PropTypes.object,
		otherFields: PropTypes.object,
		errors: PropTypes.object
	};

	checkConditionalLogic( logic ) {
		let pass = false;

		const maybeNumber = value => {
			const intVal = parseInt( value );
			return isNaN( intVal ) ? value : intVal;
		};

		logic.forEach( or_condition => {
			let condition_pass = true;
			or_condition.forEach( and_condition => {
				let checkValue = null;
				if ( this.props.fields[ and_condition.field ] ) {
					checkValue = this.props.fields[ and_condition.field ].value;
                } else if ( this.props.otherFields[ and_condition.field ] ) {
					// "otherFields" allows for us to pass in fields that we're not rendering but which might affect conditional logic
					checkValue = this.props.otherFields[ and_condition.field ].value;
                }
				const valuesAreEqual = maybeNumber( checkValue ) === maybeNumber( and_condition.value );
				switch ( and_condition.operator ) {
					case '!=':
						condition_pass = condition_pass && ! valuesAreEqual;
						break;
					case '==':
					default:
						condition_pass = condition_pass && valuesAreEqual;
						break;
				}
			});
			pass = pass || condition_pass;
		});
		return pass;
	}

	render() {
		let tabs = [], tab = null;
		return this.props.fields ? map(this.props.fields, (field, name) => {
			if ( field.type === 'tab' && tab ) {
				// If we already have a tab object, then we need to add that to our tabs array
				tabs.push( tab );
			}

			// First, we'll check if this field should be included, based on conditional logic
			if ( field.conditional_logic && ! this.checkConditionalLogic( field.conditional_logic ) ) {
				if ( field.type === 'tab' ) {
					tab = false; // false is a special indicator to say that this tab has been excluded via conditional logic.
				}
				return null;
			}

			// Next, we'll check if the field is a tab.
			if ( field.type === 'tab' ) {
				if ( field.endpoint ) {
					// If this is the endpoint marker for the tab set, then reset tab to null, and if we have tabs
					// to render, then render and return those.  Otherwise, just return nothing
					tab = null;
					if ( tabs.length ) {
						const key = tabs[0].key;
						const renderedTabs = <Tabs id={key} key={key} animation={false} onSelect={ tab => { this.props.onTabSelect && this.props.onTabSelect( tab ); }}>
							{tabs.map( ( tab, index ) => <Tab eventKey={tab.key} key={index} title={tab.label}>
								{tab.fields.map( tabField => tabField )}
							</Tab>)}
						</Tabs>;
						tabs = [];
						return renderedTabs;
					}
				} else {
					tab = {
						...field,
						fields: []
					};
				}
				return null;
			} else if ( tab === false ) {
				// We have a field under a tab that has been excluded via conditional logic.
				return null;
			}

			// Render the field into a variable.  This will either get returned outright, or will be pushed to the fields
			// of the current tab, if there is a current tab
			const wrapperClasses = classNames({
				'tbk--form-fields__field-wrapper': true,
				[name]: true,
				'required': field && !!field.required,
				'tbk--form-fields__invalid': this.props.errors && this.props.errors[ name ]
			});
			const rendered = <div key={name} className={wrapperClasses}>
				<FormField field={field} />
				{this.props.errors && this.props.errors[name] &&
				<div className="tbk--form-fields__error">{this.props.errors[name]}</div>
				}
				{field && field.description &&
				<div className="tbk--form-fields__description">{field.description}</div>
				}
			</div>;

			if ( tab ) {
				tab.fields.push( rendered );
				return null;
			} else {
				return rendered;
			}
		}) : '';
	}

}

export {FormFields, FormField};
